import React, { Component } from "react"
import { PropTypes } from "prop-types"
import { scaleLinear } from "d3"

import { Box } from "../../../ui"
import { Slider } from "../.."

import Chart from "./Chart"
import Axis from "./Axis"

const sliderDefaults = {
  showSnapHints: true,
  interpolate: false,
  tickValues: "on",
  duration: 750,
  playMode: true,
}

/**
 * Chart component for beeswarm plots with timeseries support
 */
export class Beeswarm extends Component {
  static propTypes = {
    /** Data object with the time series and the domain */
    data: PropTypes.object,
    /** Width of the chart */
    width: PropTypes.number,
    /** Height of the chart */
    height: PropTypes.number,
    /** The time to display from the time series */
    time: PropTypes.number,
    /** Custom component for the symbol */
    symbol: PropTypes.func,
    /** Symbol's width and height */
    symbolSize: PropTypes.number,
    /** Function to format tooltip value */
    tooltipFormat: PropTypes.func,
    /** Array specifing tooltip offset [x, y] */
    tooltipOffset: PropTypes.array,
    /** Array with label values */
    labels: PropTypes.arrayOf(PropTypes.string),
    /** Custom component for labels */
    labelComponent: PropTypes.func,
    /** Domain if not supplied in the data object */
    domain: PropTypes.arrayOf(PropTypes.number),
    /** Whether to display slider or not */
    slider: PropTypes.bool,
    /** Configuration passed to the slider */
    sliderProps: PropTypes.object,
    /** Radius option for the beeswarm plugin, defaults to symbolSize / 2 */
    radius: PropTypes.number,
    /** Side option for the beeswarm plugin */
    side: PropTypes.oneOf(["negative", "positive", "symetric"]),
    /** Array of ids for the selected symbols */
    selected: PropTypes.arrayOf(PropTypes.string),
    /** Key of the value property in data object */
    keyValue: PropTypes.string,
    /** Whether to display tick values */
    tickValues: PropTypes.oneOf(["auto", "on", "off"]),
    /** Custom component for labels */
    tickComponent: PropTypes.func,
    /** Number of ticks */
    tickNumbers: PropTypes.number,
    /** Custom array of tick values */
    ticks: PropTypes.array,
  }

  static defaultProps = {
    data: {
      byTime: {
        1900: [
          { id: "A", value: 25 },
          { id: "B", value: 27 },
          { id: "C", value: 75 },
        ],
        2000: [
          { id: "A", value: 50 },
          { id: "B", value: 27 },
          { id: "C", value: 25 },
        ],
      },
      domain: [0, 100],
    },
    symbolSize: 30,
    side: "symetric",
    height: 200,
    slider: true,
    sliderProps: {},
    selected: [],
    keyValue: "value",
    tickValues: "auto",
  }

  constructor(props) {
    super()
    const times = Object.keys(props.data.byTime).map(d => +d)
    const time = props.time || times[times.length - 1]
    this.state = {
      time,
      width: props.width,
      labelWidths: [0, 0],
    }

    this.labels = []
    this.updateWidth = this.updateWidth.bind(this)
  }

  componentDidMount() {
    this.updateWidth()
    window.addEventListener("resize", this.updateWidth)
  }

  componentDidUpdate() {
    this.updateWidth()
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateWidth)
  }

  updateWidth() {
    const labelWidths = [
      this.labels[0] ? this.labels[0].offsetWidth : 0,
      this.labels[1] ? this.labels[1].offsetWidth : 0,
    ]
    const width = this.chart.offsetWidth - labelWidths[0] - labelWidths[1]

    if (
      width !== this.state.width ||
      labelWidths[0] !== this.state.labelWidths[0] ||
      labelWidths[1] !== this.state.labelWidths[1]
    )
      this.setState({ width, labelWidths })
  }

  handleYearChange = time => {
    this.setState({ time })
  }

  getChartRef = el => {
    if (el) this.chart = el
  }

  getLabelRef = index => label => {
    if (label) this.labels[index] = label
  }

  render() {
    const {
      data,
      symbol,
      symbolSize,
      height,
      tooltipFormat,
      tooltipOffset,
      labels,
      labelComponent,
      slider,
      selected,
      keyValue,
      tickValues,
      tickComponent,
      tickNumbers,
      ticks,
      side,
    } = this.props
    const { width, time, labelWidths } = this.state
    const times = Object.keys(data.byTime)
      .map(d => +d)
      .sort((a, b) => a - b)
    const sliderProps = Object.assign(sliderDefaults, this.props.sliderProps)

    const radius =
      typeof this.props.radius !== "undefined"
        ? this.props.radius
        : symbolSize / 2 - 1
    const domain = this.props.domain || data.domain
    // base for the position calculations
    const top =
      side === "positive" ? 0 : side === "negative" ? height : height / 2
    const scale = scaleLinear().domain(domain)

    return (
      <Box position="relative">
        <Axis
          labels={labels}
          getLabelRef={this.getLabelRef}
          labelComponent={labelComponent}
          top={top}
          domain={domain}
          tickValues={tickValues}
          tickComponent={tickComponent}
          tickNumbers={tickNumbers}
          ticks={ticks}
          scale={scale}
          width={width}
          labelWidths={labelWidths}
        />

        <Box ref={this.getChartRef}>
          <Box position="relative" ml={`${labelWidths[0]}px`}>
            <Chart
              data={data.byTime[time]}
              width={width}
              height={height}
              symbol={symbol}
              symbolSize={symbolSize}
              scale={scale}
              tooltipFormat={tooltipFormat}
              tooltipOffset={tooltipOffset}
              side={side}
              radius={radius}
              selected={selected}
              keyValue={keyValue}
              top={top}
            />
          </Box>
        </Box>

        <Box p="2rem 1rem 0">
          {slider && times.length && (
            <Slider
              customDomain={times}
              value={time}
              onChange={this.handleYearChange}
              {...sliderProps}
            />
          )}
        </Box>
      </Box>
    )
  }
}
